/**
 * System Data Administration Service
 * Handles API calls for managing Offices, Departments, and Positions
 */

import axiosInstance from '../utils/axiosConfig';
import keepServerAnswer from '../utils/keepServerAnswer';

const systemDataService = {
  /**
   * Get overview of all system data categories with counts
   */
  getSystemDataOverview: async () => {
    try {
      const response = await axiosInstance.get('/api/admin/hms/system-data/');
      return response.data;
    } catch (error) {
      console.error('Error fetching system data overview:', error);
      throw error;
    }
  },

  // ============================================================================
  // OFFICES
  // ============================================================================

  /**
   * Get all offices
   */
  getOffices: async () => {
    try {
      const response = await axiosInstance.get('/api/admin/hms/system-data/offices/');
      return response.data.offices || [];
    } catch (error) {
      console.error('Error fetching offices:', error);
      throw error;
    }
  },

  /**
   * Get single office by ID
   */
  getOffice: async (officeId) => {
    try {
      const response = await axiosInstance.get(`/api/admin/hms/system-data/offices/${officeId}/`);
      return response.data.office;
    } catch (error) {
      console.error('Error fetching office:', error);
      throw error;
    }
  },

  /**
   * Create new office
   */
  createOffice: async (officeData) => {
    try {
      const response = await axiosInstance.post('/api/admin/hms/system-data/offices/', officeData);
      return response.data;
    } catch (error) {
      console.error('Error creating office:', error);
      throw error;
    }
  },

  /**
   * Update office
   */
  updateOffice: async (officeId, officeData) => {
    try {
      const response = await axiosInstance.patch(`/api/admin/hms/system-data/offices/${officeId}/`, officeData);
      return response.data;
    } catch (error) {
      console.error('Error updating office:', error);
      throw error;
    }
  },

  /**
   * Delete office (sets affected employees' office to NULL)
   */
  deleteOffice: async (officeId) => {
    try {
      const response = await axiosInstance.delete(`/api/admin/hms/system-data/offices/${officeId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting office:', error);
      throw error;
    }
  },

  // ============================================================================
  // OFFICE VAT RATES
  // ============================================================================

  getOfficeVatRates: async (officeId) => {
    try {
      const response = await axiosInstance.get(`/api/admin/hms/system-data/offices/${officeId}/vat-rates/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching office VAT rates:', error);
      throw error;
    }
  },

  createOfficeVatRate: async (officeId, vatRateData) => {
    try {
      const response = await axiosInstance.post(`/api/admin/hms/system-data/offices/${officeId}/vat-rates/`, vatRateData);
      return response.data;
    } catch (error) {
      console.error('Error creating VAT rate:', error);
      throw error;
    }
  },

  updateOfficeVatRates: async (officeId, allowedVatRateIds) => {
    try {
      const response = await axiosInstance.put(`/api/admin/hms/system-data/offices/${officeId}/vat-rates/`, {
        allowed_vat_rate_ids: allowedVatRateIds
      });
      return response.data;
    } catch (error) {
      console.error('Error updating office VAT rates:', error);
      throw error;
    }
  },

  // ============================================================================
  // CRM PROFILES FOR OFFICE LINKING
  // ============================================================================

  /**
   * HMS entities available to link an office to. Travels on the offices
   * response, so this never fires a second request.
   */
  getHMSEntitiesForLinking: async () => {
    try {
      const response = await axiosInstance.get('/api/admin/hms/system-data/offices/');
      return response.data.hms_entities || [];
    } catch (error) {
      console.error('Error fetching HMS entities for linking:', error);
      // Row 128: this returned [], so a LINKED office's edit form showed
      // "-- Not linked --". A failed load is not an empty list.
      throw keepServerAnswer(error, "Couldn't load the list of IMT companies.");
    }
  },

  getCRMProfilesForLinking: async () => {
    try {
      const response = await axiosInstance.get('/api/admin/hms/system-data/crm-profiles-for-linking/');
      return response.data.profiles || [];
    } catch (error) {
      console.error('Error fetching CRM profiles for linking:', error);
      throw error;
    }
  },

  // ============================================================================
  // DEPARTMENTS
  // ============================================================================

  /**
   * Get all departments
   */
  getDepartments: async () => {
    try {
      const response = await axiosInstance.get('/api/admin/hms/system-data/departments/');
      return response.data.departments || [];
    } catch (error) {
      console.error('Error fetching departments:', error);
      throw error;
    }
  },

  /**
   * Get single department by ID
   */
  getDepartment: async (departmentId) => {
    try {
      const response = await axiosInstance.get(`/api/admin/hms/system-data/departments/${departmentId}/`);
      return response.data.department;
    } catch (error) {
      console.error('Error fetching department:', error);
      throw error;
    }
  },

  /**
   * Create new department
   */
  createDepartment: async (departmentData) => {
    try {
      const response = await axiosInstance.post('/api/admin/hms/system-data/departments/', departmentData);
      return response.data;
    } catch (error) {
      console.error('Error creating department:', error);
      throw error;
    }
  },

  /**
   * Update department
   */
  updateDepartment: async (departmentId, departmentData) => {
    try {
      const response = await axiosInstance.patch(`/api/admin/hms/system-data/departments/${departmentId}/`, departmentData);
      return response.data;
    } catch (error) {
      console.error('Error updating department:', error);
      throw error;
    }
  },

  /**
   * Delete department (sets affected employees' department to NULL)
   */
  deleteDepartment: async (departmentId) => {
    try {
      const response = await axiosInstance.delete(`/api/admin/hms/system-data/departments/${departmentId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting department:', error);
      throw error;
    }
  },

  // ============================================================================
  // POSITIONS
  // ============================================================================

  /**
   * Get all positions
   */
  getPositions: async () => {
    try {
      const response = await axiosInstance.get('/api/admin/hms/system-data/positions/');
      return response.data.positions || [];
    } catch (error) {
      console.error('Error fetching positions:', error);
      throw error;
    }
  },

  /**
   * Get single position by ID
   */
  getPosition: async (positionId) => {
    try {
      const response = await axiosInstance.get(`/api/admin/hms/system-data/positions/${positionId}/`);
      return response.data.position;
    } catch (error) {
      console.error('Error fetching position:', error);
      throw error;
    }
  },

  /**
   * Create new position
   */
  createPosition: async (positionData) => {
    try {
      const response = await axiosInstance.post('/api/admin/hms/system-data/positions/', positionData);
      return response.data;
    } catch (error) {
      console.error('Error creating position:', error);
      throw error;
    }
  },

  /**
   * Update position
   */
  updatePosition: async (positionId, positionData) => {
    try {
      const response = await axiosInstance.patch(`/api/admin/hms/system-data/positions/${positionId}/`, positionData);
      return response.data;
    } catch (error) {
      console.error('Error updating position:', error);
      throw error;
    }
  },

  /**
   * Delete position (sets affected employees' position to NULL)
   */
  deletePosition: async (positionId) => {
    try {
      const response = await axiosInstance.delete(`/api/admin/hms/system-data/positions/${positionId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting position:', error);
      throw error;
    }
  },

  // ============================================================================
  // UNITS
  // ============================================================================

  /**
   * Get all units
   */
  getUnits: async () => {
    try {
      const response = await axiosInstance.get('/api/admin/hms/system-data/units/');
      return response.data.units || [];
    } catch (error) {
      console.error('Error fetching units:', error);
      throw error;
    }
  },

  /**
   * Get single unit by ID
   */
  getUnit: async (unitId) => {
    try {
      const response = await axiosInstance.get(`/api/admin/hms/system-data/units/${unitId}/`);
      return response.data.unit;
    } catch (error) {
      console.error('Error fetching unit:', error);
      throw error;
    }
  },

  /**
   * Create new unit
   */
  createUnit: async (unitData) => {
    try {
      const response = await axiosInstance.post('/api/admin/hms/system-data/units/', unitData);
      return response.data;
    } catch (error) {
      console.error('Error creating unit:', error);
      throw error;
    }
  },

  /**
   * Update unit
   */
  updateUnit: async (unitId, unitData) => {
    try {
      const response = await axiosInstance.patch(`/api/admin/hms/system-data/units/${unitId}/`, unitData);
      return response.data;
    } catch (error) {
      console.error('Error updating unit:', error);
      throw error;
    }
  },

  /**
   * Delete unit (only if not in use)
   */
  deleteUnit: async (unitId) => {
    try {
      const response = await axiosInstance.delete(`/api/admin/hms/system-data/units/${unitId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting unit:', error);
      throw error;
    }
  },

  getBranches: async () => {
    try {
      const response = await axiosInstance.get('/api/fuel-enquiries/branches/');
      return Array.isArray(response.data) ? response.data : (response.data?.results || []);
    } catch (error) {
      console.error('Error fetching IMT branches:', error);
      throw error;
    }
  },

  createBranch: async (branchData) => {
    const response = await axiosInstance.post('/api/fuel-enquiries/branches/', branchData);
    return response.data;
  },

  updateBranch: async (branchId, branchData) => {
    const response = await axiosInstance.patch(
      `/api/fuel-enquiries/branches/${branchId}/`,
      branchData,
    );
    return response.data;
  },

  deleteBranch: async (branchId) => {
    const response = await axiosInstance.delete(`/api/fuel-enquiries/branches/${branchId}/`);
    return response.data;
  },

  staffBranch: async (branchId, staffingPayload) => {
    const response = await axiosInstance.post(
      `/api/fuel-enquiries/branches/${branchId}/staff/`,
      staffingPayload,
    );
    return response.data;
  },

  /**
   * Get a page of ports (search/filter + pagination, all server-side).
   * @param {Object} opts
   * @param {string} [opts.search] — substring match against port_name, unlocode, country_name
   * @param {boolean} [opts.bunkerOnly] — restrict to ports on the bunker list
   * @param {boolean} [opts.cargoOnly] — restrict to ports on the cargo list
   * @param {number} [opts.limit=50] — page size
   * @param {number} [opts.offset=0] — number of rows to skip
   * @returns {Promise<{ports: Array, total: number}>} the page plus the full filtered count
   */
  getPorts: async ({ search = '', bunkerOnly = false, cargoOnly = false, limit = 50, offset = 0 } = {}) => {
    try {
      const params = { limit, offset };
      if (search) params.search = search;
      if (bunkerOnly) params.bunker_only = 'true';
      if (cargoOnly) params.cargo_only = 'true';
      const response = await axiosInstance.get('/api/admin/hms/system-data/ports/', { params });
      const ports = response.data.ports || [];
      return { ports, total: response.data.total ?? ports.length };
    } catch (error) {
      console.error('Error fetching ports:', error);
      throw error;
    }
  },

  createPort: async (portData) => {
    const response = await axiosInstance.post('/api/admin/hms/system-data/ports/', portData);
    return response.data;
  },

  updatePort: async (portId, portData) => {
    const response = await axiosInstance.patch(
      `/api/admin/hms/system-data/ports/${portId}/`,
      portData,
    );
    return response.data;
  },

  deletePort: async (portId) => {
    const response = await axiosInstance.delete(
      `/api/admin/hms/system-data/ports/${portId}/`,
    );
    return response.data;
  },

  bulkUploadPorts: async (csvFile) => {
    const formData = new FormData();
    formData.append('csv_file', csvFile);
    const response = await axiosInstance.post(
      '/api/admin/hms/system-data/ports/bulk-upload/',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },
};

export default systemDataService;

